import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import {
  LayoutDashboard,
  Receipt,
  Users,
  ChefHat,
  FolderKanban,
  TrendingUp,
  CreditCard,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Plus,
  Sparkles,
  Crown,
  AlertTriangle,
  Wallet,
  FileSpreadsheet
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import QuickEntryDialog from './QuickEntryDialog';

const allNavItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['owner', 'staff', 'accountant'] },
  { path: '/transactions', icon: Receipt, label: 'Transactions', roles: ['owner', 'staff', 'accountant'] },
  { path: '/ingredients', icon: ChefHat, label: 'Ingredients', roles: ['owner', 'staff', 'accountant'] },
  { path: '/projects', icon: FolderKanban, label: 'Projects', roles: ['owner', 'accountant'] },
  { path: '/investments', icon: TrendingUp, label: 'Investments', roles: ['owner', 'accountant'] },
  { path: '/investment-ledger', icon: Wallet, label: 'Capital Ledger', roles: ['owner', 'accountant'] },
  { path: '/credit-cards', icon: CreditCard, label: 'Credit Cards', roles: ['owner', 'accountant'] },
  { path: '/expense-sheet', icon: FileSpreadsheet, label: 'Expense Sheet', roles: ['owner', 'accountant'] },
  { path: '/reports', icon: FileText, label: 'Reports', roles: ['owner', 'accountant'] },
  { path: '/exception-review', icon: AlertTriangle, label: 'Exception Review', roles: ['owner', 'accountant'] },
  { path: '/settings', icon: Settings, label: 'Settings', roles: ['owner'] }
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [quickEntryOpen, setQuickEntryOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const userRole = user?.role || 'staff';
  const navItems = allNavItems.filter(item => item.roles.includes(userRole));
  const mainNav = navItems.filter(item => ['/dashboard', '/transactions', '/ingredients', '/projects'].includes(item.path));
  const insightNav = navItems.filter(item => !['/dashboard', '/transactions', '/ingredients', '/projects'].includes(item.path));

  return (
    <div className="app-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
                <ChefHat className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="logo-text text-zinc-900">ROCH</span>
                <p className="text-[10px] text-amber-600 font-medium tracking-wide">HOSPITALITY</p>
              </div>
            </div>
            <button 
              className="lg:hidden p-2 hover:bg-zinc-100 rounded-lg"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4">
            <div className="sidebar-section">
              <div className="sidebar-section-label">Main</div>
            </div>
            {mainNav.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}

            {insightNav.length > 0 && (
              <>
                <div className="sidebar-section mt-6">
                  <div className="sidebar-section-label">Insights</div>
                </div>
                {insightNav.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </>
            )}

            {/* Role badge */}
            {userRole !== 'owner' && (
              <div className="mx-4 mt-6 px-3 py-2 bg-zinc-50 rounded-lg">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-medium">Role</p>
                <p className="text-xs text-zinc-600 font-medium capitalize">{userRole}</p>
              </div>
            )}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-zinc-100">
            <div className="flex items-center gap-3 mb-3">
              <Avatar className="w-10 h-10">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-zinc-100 text-zinc-600 text-sm font-medium">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-900 truncate">{user?.name}</p>
                <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              className="w-full justify-start gap-2 text-zinc-500 hover:text-zinc-900"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className={`main-content ${!sidebarOpen ? 'sidebar-hidden' : ''}`}>
        {/* Mobile header */}
        <header className="glass-header lg:hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <button 
              className="p-2 hover:bg-zinc-100 rounded-lg"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <ChefHat className="w-5 h-5 text-zinc-900" />
              <span className="font-semibold">ROCH</span>
            </div>
            <Avatar className="w-8 h-8">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-zinc-100 text-zinc-600 text-xs">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 sm:p-6 md:p-8 pt-16 lg:pt-6">
          {children}
        </div>
      </main>

      {/* Quick Entry FAB - only for roles that can create */}
      {userRole !== 'accountant' && (
        <button 
          className="quick-entry-btn"
          onClick={() => setQuickEntryOpen(true)}
          data-testid="quick-entry-btn"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      {/* Quick Entry Dialog */}
      {userRole !== 'accountant' && (
        <QuickEntryDialog open={quickEntryOpen} onOpenChange={setQuickEntryOpen} />
      )}
    </div>
  );
}
